import { useState } from "react";
import prettyBytes from "pretty-bytes";
import { toast } from "react-toastify";
import { Switch } from "@headlessui/react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa6";

import type { ChangeStorageTierParams } from "@/apiClient/models/ChangeStorageTierParams";
import type { DataAssetSearchParameters } from "@/apiClient/models/DataAssetSearchParameters";
import type { StorageTierPreviewResponse } from "@/apiClient/models/StorageTierPreviewResponse";
import { ManifestType } from "@/apiClient/models/ManifestType";
import { StorageTier } from "@/apiClient/models/StorageTier";
import useApiClient from "@/hooks/useApiClient";
import ManifestDropdown from "@/components/manifest/manifestsDropdowns";
import DataSetDropdown from "@/components/dataSet/dataSetDropdowns";
import RealAssetDeploymentTreeDropdown from "@/components/realAsset/realAssetDeploymentTreeDropdown";
import FileTypeDropdown, {
  mapFileTypeToFileExtensions,
} from "@/components/dropdowns/fileTypeDropdown";
import MultiSelectDropdown from "@/components/dropdowns/multiSelectDropdown";
import { dropdownHandlerForQueryParamsFactory } from "@/components/dropdowns/utils";
import { manifestLabel } from "@/components/folderConfig/folderConfig.utils";
import FilterChip from "@/components/filterChip";
import FilterChipList from "@/components/filterChip/filterChipList";
import SmartFilterChip from "@/components/filterChip/smartFilterChip";
import AssetPathList, {
  useAssetPathsByPublicId,
} from "@/components/storageOps/AssetPathList";
import {
  TIER_OPTIONS,
  parseDelimitedList,
  storageOpsSearchDefaults,
  type StorageOpsSearch,
} from "@/components/storageOps/storageOps.utils";

/** The subset of URL search params owned by the tier-change form. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { groupByManifestType: _groupByManifestType, ...TIER_CHANGE_FORM_DEFAULTS } =
  storageOpsSearchDefaults;

const buildSearchParameters = (
  form: StorageOpsSearch
): DataAssetSearchParameters => {
  const search: DataAssetSearchParameters = {};

  if (form.manifestPublicIds.length > 0) {
    search.manifest_public_ids = form.manifestPublicIds;
  }
  if (form.manifestTypes.length > 0) {
    search.manifest_types = form.manifestTypes;
  }
  if (form.realAssetDeploymentPublicIds.length > 0) {
    search.real_asset_deployment_public_ids =
      form.realAssetDeploymentPublicIds;
  }
  if (form.dataSetPublicIds.length > 0) {
    search.data_set_public_ids = form.dataSetPublicIds;
  }
  if (form.pathQuery.trim()) {
    search.query = form.pathQuery.trim();
    if (form.regex) {
      search.regex = true;
    }
  }
  // Same file-type → extension expansion the asset-search page uses.
  const fileExtensions = mapFileTypeToFileExtensions(form.fileTypes)[1];
  if (Array.isArray(fileExtensions) && fileExtensions.length > 0) {
    search.file_extensions = fileExtensions;
  }
  const dataAssetPublicIds = parseDelimitedList(form.dataAssetPublicIds);
  if (dataAssetPublicIds.length > 0) {
    search.data_asset_public_ids = dataAssetPublicIds;
  }

  return search;
};

const hasAnySearchCriteria = (search: DataAssetSearchParameters): boolean =>
  Object.keys(search).length > 0;

/** Sample early-deletion assets rendered as file paths (one batched lookup). */
function SampleEarlyDeletionAssets({ publicIds }: { publicIds: string[] }) {
  const pathByPublicId = useAssetPathsByPublicId(publicIds);
  return <AssetPathList publicIds={publicIds} pathByPublicId={pathByPublicId} />;
}

function PreviewSummary({ preview }: { preview: StorageTierPreviewResponse }) {
  return (
    <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/70 p-4">
      <p className="text-sm font-semibold text-blue-200">
        Preview: move {preview.matched_links.toLocaleString()} links (
        {prettyBytes(preview.total_bytes)}) to {preview.target_tier}
      </p>

      {preview.breakdown.length > 0 && (
        <table className="w-full text-left text-xs text-blue-300">
          <thead>
            <tr className="border-b border-blue-100 text-[10px] font-bold uppercase tracking-[0.08em] text-blue-100">
              <th className="py-1 pr-3">Current Tier</th>
              <th className="py-1 pr-3 text-right">Links</th>
              <th className="py-1 pr-3 text-right">Size</th>
              <th className="py-1 text-right">Early-Deletion Exposure</th>
            </tr>
          </thead>
          <tbody>
            {preview.breakdown.map((row) => (
              <tr key={row.current_tier} className="border-b border-blue-50">
                <td className="py-1 pr-3">{row.current_tier}</td>
                <td className="py-1 pr-3 text-right">
                  {row.links.toLocaleString()}
                </td>
                <td className="py-1 pr-3 text-right">
                  {prettyBytes(row.bytes)}
                </td>
                <td className="py-1 text-right">
                  {row.early_deletion_links > 0
                    ? `${row.early_deletion_links.toLocaleString()} links / ${prettyBytes(row.early_deletion_bytes)}`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {preview.retrieval_bytes > 0 && (
        <p className="text-xs text-blue-300">
          Retrieval required for {prettyBytes(preview.retrieval_bytes)} of
          archived data (rehydration takes 1–15 hours).
        </p>
      )}

      {preview.early_deletion_links > 0 && (
        <div className="rounded-md border border-amber-100 bg-amber-50 p-3 text-xs text-amber-700">
          <p className="font-semibold">
            {preview.early_deletion_links.toLocaleString()} links (
            {prettyBytes(preview.early_deletion_bytes)}) would incur
            early-deletion fees.
          </p>
          {preview.sample_early_deletion_asset_public_ids.length > 0 && (
            <SampleEarlyDeletionAssets
              publicIds={preview.sample_early_deletion_asset_public_ids}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TierChangeRequestPanel({
  search: form,
  updateSearch,
  onPreview,
  onSubmit,
}: {
  search: StorageOpsSearch;
  updateSearch: (update: Partial<StorageOpsSearch>) => void;
  onPreview: (
    body: ChangeStorageTierParams
  ) => Promise<StorageTierPreviewResponse>;
  onSubmit: (body: ChangeStorageTierParams) => Promise<unknown>;
}) {
  const api = useApiClient();
  // Preview results are server data and deliberately stay out of the URL;
  // a pasted link reproduces the form and the operator re-previews.
  const [preview, setPreview] = useState<StorageTierPreviewResponse | null>(
    null
  );
  const [previewedBodyKey, setPreviewedBodyKey] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateForm = (update: Partial<StorageOpsSearch>) => {
    // Any edit invalidates the current fee acknowledgement; the dry run
    // itself invalidates via the bodyKey comparison below.
    updateSearch({ ...update, allowFees: false });
  };

  // Same toggle semantics as the asset-search page's dropdown handlers.
  const makeDropdownChangeHandler = dropdownHandlerForQueryParamsFactory({
    rawValues: form,
    setRawValues: updateForm,
  });
  const handleManifestSelection =
    makeDropdownChangeHandler("manifestPublicIds");
  const handleManifestTypeSelection =
    makeDropdownChangeHandler("manifestTypes");
  const handleDeploymentSelection = makeDropdownChangeHandler(
    "realAssetDeploymentPublicIds"
  );
  const handleDataSetSelection = makeDropdownChangeHandler("dataSetPublicIds");
  const handleFileTypesSelection = makeDropdownChangeHandler("fileTypes");

  const search = buildSearchParameters(form);
  const searchIsRunnable = hasAnySearchCriteria(search);
  const body: ChangeStorageTierParams = {
    search,
    target_tier: form.targetTier,
    allow_early_deletion_fees: form.allowFees,
  };
  const bodyKey = JSON.stringify({ search, target_tier: form.targetTier });
  const previewIsCurrent = preview !== null && previewedBodyKey === bodyKey;

  const needsFeeAcknowledgement =
    previewIsCurrent && preview.early_deletion_links > 0;
  const canSubmit =
    previewIsCurrent &&
    preview.matched_links > 0 &&
    (!needsFeeAcknowledgement || form.allowFees) &&
    !isSubmitting;

  const handlePreview = async () => {
    setIsPreviewing(true);
    try {
      const result = await onPreview(body);
      setPreview(result);
      setPreviewedBodyKey(bodyKey);
    } catch (error) {
      toast.error(
        `Preview failed: ${error instanceof Error ? error.message : error}`
      );
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(body);
      toast.success("Tier-change job submitted.");
      updateSearch(TIER_CHANGE_FORM_DEFAULTS);
      setPreview(null);
      setPreviewedBodyKey(null);
    } catch (error) {
      toast.error(
        `Submit failed: ${error instanceof Error ? error.message : error}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mirrors the asset-search page's chips so operators can sanity-check
  // scope before previewing/submitting.
  const filterChips = [
    {
      title: "Manifests",
      items: form.manifestPublicIds,
      maxVisible: 4,
      onClose: handleManifestSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Manifest"
          selection={publicId}
          queryKey={["manifestMetadata", publicId]}
          onClose={() => handleManifestSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: { name?: string }) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataTransfer.getManifestMetadata({ publicId })}
        />
      ),
    },
    {
      title: "Manifest Types",
      items: form.manifestTypes,
      maxVisible: 4,
      onClose: handleManifestTypeSelection,
      renderChip: (manifestType: string) => (
        <FilterChip
          key={manifestType}
          title="Manifest Type"
          label={manifestLabel(manifestType)}
          onClose={() => handleManifestTypeSelection(manifestType)}
        />
      ),
    },
    {
      title: "Real Asset Deployments",
      items: form.realAssetDeploymentPublicIds,
      maxVisible: 4,
      onClose: handleDeploymentSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Deployment"
          selection={publicId}
          queryKey={["realAssetDeploymentDetail", publicId]}
          onClose={() => handleDeploymentSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: {
            name?: string | null;
          }) => response?.name ?? ""}
          queryFn={() => api.dataAccess.realAssetDeploymentDetail({ publicId })}
        />
      ),
    },
    {
      title: "Data Sets",
      items: form.dataSetPublicIds,
      maxVisible: 4,
      onClose: handleDataSetSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Data Set"
          selection={publicId}
          queryKey={["dataSetDetail", publicId]}
          onClose={() => handleDataSetSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: { name?: string }) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.dataSetDetail({ publicId })}
        />
      ),
    },
    {
      title: "File Type",
      items: form.fileTypes,
      maxVisible: 4,
      onClose: handleFileTypesSelection,
    },
  ];
  const hasFilterChipActive = filterChips.some(
    (chip) => chip.items.length > 0
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-3">
        <h2 className="text-lg font-semibold text-blue-200">
          Request Tier Change
        </h2>
        <p className="text-xs text-grey-200">
          Build a search, preview the dry run, then submit. Nothing changes
          until you submit.
        </p>
      </div>

      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="z-40 w-[14rem] shrink-0">
            <ManifestDropdown
              selections={form.manifestPublicIds}
              onOptionToggle={handleManifestSelection}
              renderOption={(manifest) =>
                manifest.manifest_type
                  ? `${manifest.name} — ${manifest.manifest_type}`
                  : manifest.name
              }
              className="w-[14rem]"
            />
          </div>
          <div className="z-40 w-[14rem] shrink-0">
            <MultiSelectDropdown<ManifestType, ManifestType>
              label="Manifest Types"
              options={Object.values(ManifestType)}
              selections={form.manifestTypes}
              isLoading={false}
              renderOption={manifestLabel}
              onSearchTermChange={() => undefined}
              enableSearch={false}
              mapOptionToSelection={(option) => option}
              onOptionToggle={handleManifestTypeSelection}
              className="w-[14rem]"
            />
          </div>
          <div className="z-40 w-[14rem] shrink-0">
            <RealAssetDeploymentTreeDropdown
              selectedIds={form.realAssetDeploymentPublicIds}
              onOptionToggle={handleDeploymentSelection}
              labelClassName="w-[14rem] shrink-0"
              dropdownClassName="min-w-[22rem]"
            />
          </div>
          <div className="z-40 w-[10.125rem] shrink-0">
            <DataSetDropdown
              selections={form.dataSetPublicIds}
              onOptionToggle={handleDataSetSelection}
              className="w-[10.125rem]"
            />
          </div>
          <div className="z-40 w-[10rem] shrink-0">
            <FileTypeDropdown
              dropdownProps={{
                selections: form.fileTypes,
                onOptionToggle: handleFileTypesSelection,
                className: "w-[10rem] shrink-0",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block text-sm text-blue-300">
            Path query
            <input
              type="text"
              className="form-input mt-1 block w-full rounded-md text-blue-300"
              placeholder="Search in file paths"
              value={form.pathQuery}
              onChange={(event) =>
                updateForm({ pathQuery: event.target.value })
              }
            />
          </label>

          <div className="flex items-end gap-6">
            <label className="block flex-1 text-sm text-blue-300">
              Target tier
              <select
                className="form-select mt-1 block w-full rounded-md text-blue-300"
                value={form.targetTier}
                onChange={(event) =>
                  updateForm({ targetTier: event.target.value as StorageTier })
                }
              >
                {TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </label>

            <label className="mb-2 flex items-center text-sm text-blue-300">
              <span className="mr-2">Regex</span>
              <Switch
                checked={form.regex}
                onChange={(regex: boolean) => updateForm({ regex })}
                className={`${form.regex ? "bg-blue-200" : "bg-gray-200"}
                  relative inline-flex h-6 w-11 items-center
                  rounded-full p-[3px] transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    form.regex ? "translate-x-[1.375rem]" : "translate-x-0"
                  }
                  inline-block h-4 w-4 transform rounded-full bg-white
                  transition-transform duration-200 ease-in-out`}
                />
              </Switch>
            </label>
          </div>
        </div>

        <div>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between"
            aria-expanded={form.advancedOpen}
            onClick={() => updateSearch({ advancedOpen: !form.advancedOpen })}
          >
            <p className="text-sm text-blue-200">Advanced</p>
            {form.advancedOpen ? (
              <FaChevronUp size={14} />
            ) : (
              <FaChevronDown size={14} />
            )}
          </button>
          <div
            style={{
              maxHeight: form.advancedOpen ? "200px" : "0",
              transition: "max-height 0.2s ease-in-out",
            }}
            className={form.advancedOpen ? "" : "overflow-hidden"}
          >
            <label className="mt-2 block text-sm text-blue-300">
              Data asset public IDs (comma or newline separated) — for scripted
              per-asset targeting
              <textarea
                className="form-textarea mt-1 block w-full rounded-md text-blue-300"
                rows={3}
                value={form.dataAssetPublicIds}
                onChange={(event) =>
                  updateForm({ dataAssetPublicIds: event.target.value })
                }
              />
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-4 px-5 pb-5">
        {hasFilterChipActive && (
          <div className="flex flex-wrap gap-2">
            <FilterChipList filters={filterChips} />
          </div>
        )}

        {previewIsCurrent && <PreviewSummary preview={preview} />}

        {needsFeeAcknowledgement && (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-amber-700">
            <input
              type="checkbox"
              className="form-checkbox rounded text-amber-600"
              checked={form.allowFees}
              onChange={(event) =>
                updateSearch({ allowFees: event.target.checked })
              }
            />
            Allow early-deletion fees for this request
          </label>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="ox-button !mt-0 w-auto !rounded-[8px] !px-4 !py-1.5 !text-sm"
            disabled={!searchIsRunnable || isPreviewing}
            onClick={handlePreview}
          >
            {isPreviewing ? "Previewing..." : "Preview"}
          </button>
          <button
            type="button"
            className="ox-button !mt-0 w-auto !rounded-[8px] !px-4 !py-1.5 !text-sm"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          {!searchIsRunnable && (
            <p className="text-xs text-grey-200">
              Add at least one search criterion to preview.
            </p>
          )}
          {searchIsRunnable && !previewIsCurrent && (
            <p className="text-xs text-grey-200">
              Run a preview before submitting.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default TierChangeRequestPanel;
